// src/components/Dashboard.jsx
// FINAL: Calendario escolar (Agosto a Julio)

import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Dashboard = ({ actividades, meses }) => {
  // Validar que actividades es un array
  if (!actividades || !Array.isArray(actividades)) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <p className="text-gray-500">Cargando datos...</p>
      </div>
    );
  }

  // Contar actividades por estado
  const estados = ['sin iniciar', 'en proceso', 'finalizada', 'cancelada', 'programada'];
  const contarPorEstado = {};
  
  estados.forEach(estado => {
    contarPorEstado[estado] = actividades.filter(a => a && a.estado === estado).length;
  });

  // Contar por mes - CON VALIDACIONES
  const contarPorMes = {};
  meses.forEach((mes, index) => {
    contarPorMes[mes] = actividades.filter(a => {
      if (!a || !a.fechaInicio) return false;
      try {
        const mes_actividad = parseInt(a.fechaInicio.split('-')[1]);
        return mes_actividad === (index + 1);
      } catch (e) {
        console.warn('Fecha inválida:', a.fechaInicio);
        return false;
      }
    }).length;
  });

  // 🎓 ORDEN ESCOLAR: Agosto a Julio
  const ordenEscolar = [
    'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio'
  ];

  // Datos para gráfico EN ORDEN ESCOLAR
  const dataGraficoMeses = ordenEscolar.map(mes => ({
    mes: mes.substring(0, 3),
    actividades: contarPorMes[mes] || 0
  }));

  // Contar por responsable
  const contarPorResponsable = {};
  actividades.forEach(a => {
    if (a && a.responsable) {
      const responsable = a.responsable;
      contarPorResponsable[responsable] = (contarPorResponsable[responsable] || 0) + 1;
    }
  });

  // Convertir a array para la tabla
  const datosPorResponsable = Object.entries(contarPorResponsable).map(([responsable, total]) => {
    const actividadesResponsable = actividades.filter(a => a && a.responsable === responsable);
    const completadas = actividadesResponsable.filter(a => a && a.estado === 'finalizada').length;
    const progreso = total > 0 ? Math.round((completadas / total) * 100) : 0;
    
    return {
      responsable,
      total,
      completadas,
      progreso
    };
  });

  // Calcular totales
  const totalActividades = actividades.length;
  const actividadesFinalizadas = contarPorEstado['finalizada'] || 0;
  const porcentajeCompletion = totalActividades > 0 
    ? Math.round((actividadesFinalizadas / totalActividades) * 100)
    : 0;

  return (
    <div className="space-y-6 mb-6">
      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* Total */}
        <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg shadow-md p-4 text-white">
          <p className="text-sm opacity-90">Total</p>
          <p className="text-3xl font-bold">{totalActividades}</p>
          <p className="text-xs opacity-75 mt-1">Actividades</p>
        </div>

        {/* Programadas */}
        <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg shadow-md p-4 text-white">
          <p className="text-sm opacity-90">⏳ Programadas</p>
          <p className="text-3xl font-bold">{contarPorEstado['programada'] || 0}</p>
        </div>

        {/* Sin iniciar */}
        <div className="bg-gradient-to-br from-red-400 to-red-600 rounded-lg shadow-md p-4 text-white">
          <p className="text-sm opacity-90">❌ Sin Iniciar</p>
          <p className="text-3xl font-bold">{contarPorEstado['sin iniciar'] || 0}</p>
        </div>

        {/* En proceso */}
        <div className="bg-gradient-to-br from-blue-400 to-indigo-600 rounded-lg shadow-md p-4 text-white">
          <p className="text-sm opacity-90">🔄 En Proceso</p>
          <p className="text-3xl font-bold">{contarPorEstado['en proceso'] || 0}</p>
        </div>

        {/* Finalizadas */}
        <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-lg shadow-md p-4 text-white">
          <p className="text-sm opacity-90">✅ Finalizadas</p>
          <p className="text-3xl font-bold">{actividadesFinalizadas}</p>
        </div>

        {/* Canceladas */}
        <div className="bg-gradient-to-br from-gray-400 to-gray-600 rounded-lg shadow-md p-4 text-white">
          <p className="text-sm opacity-90">⛔ Canceladas</p>
          <p className="text-3xl font-bold">{contarPorEstado['cancelada'] || 0}</p>
        </div>
      </div>

      {/* Barra de progreso general */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">📊 Completitud General del Proyecto</h3>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className="bg-gradient-to-r from-blue-500 to-blue-600 h-4 rounded-full transition-all duration-300"
            style={{ width: `${porcentajeCompletion}%` }}
          ></div>
        </div>
        <div className="mt-2 flex justify-between">
          <span className="text-sm text-gray-600">{actividadesFinalizadas} de {totalActividades} finalizadas</span>
          <span className="text-sm font-bold text-blue-600">{porcentajeCompletion}%</span>
        </div>
      </div>

      {/* Gráfico de actividades por mes - ORDEN ESCOLAR */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">📅 Actividades por Mes</h3>
        <p className="text-xs text-gray-500 mb-4">🎓 Calendario Escolar (Agosto - Julio)</p>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={dataGraficoMeses}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="mes" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="actividades" fill="#3b82f6" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tabla de actividades por responsable */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">👥 Actividades por Responsable</h3>
        {datosPorResponsable.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left p-3 text-gray-600">Responsable</th>
                  <th className="text-center p-3 text-gray-600">Total</th>
                  <th className="text-center p-3 text-gray-600">Completadas</th>
                  <th className="text-center p-3 text-gray-600">Progreso</th>
                </tr>
              </thead>
              <tbody>
                {datosPorResponsable.map((item, index) => (
                  <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="p-3 text-gray-800">{item.responsable}</td>
                    <td className="text-center p-3">{item.total}</td>
                    <td className="text-center p-3">
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                        {item.completadas}
                      </span>
                    </td>
                    <td className="text-center p-3">
                      <div className="w-32 bg-gray-200 rounded-full h-2 mx-auto">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${item.progreso}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-600 mt-1">{item.progreso}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-6">Sin responsables asignados</p>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
